"use client"

import {
  ScatterChart as RScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, Line, ReferenceLine, LabelList, Cell,
} from "recharts"
import {
  type ChartDatum, type ChartDimension, DEFAULT_PALETTE,
} from "./types"
import type { CSSProperties } from "react"

export interface ScatterSeries {
  name: string
  data: ChartDatum[]
  color?: string
  /** Render each point with the listed shape. */
  shape?: "circle" | "cross" | "diamond" | "square" | "star" | "triangle" | "wye"
  /** Render a connecting line through the points. */
  withLine?: boolean
  /** Per-point color override field — when set, picks `palette[idx]` per point. */
  cellColors?: string[]
}

export interface ScatterChartProps {
  series?: ScatterSeries[]
  data?: ChartDatum[]
  xKey?: string
  yKey?: string
  /** Z field used as bubble size. When provided, this becomes a bubble plot. */
  zKey?: string
  zRange?: [number, number]
  palette?: string[]
  showLegend?: boolean
  showGrid?: boolean
  showTooltip?: boolean
  xLabel?: string
  yLabel?: string
  className?: string
  style?: CSSProperties
  height?: ChartDimension
  width?: ChartDimension
  compact?: boolean
  /** Show axis values as point labels next to each marker. */
  showLabels?: boolean
  /** Render an additional Y axis on the right (multi-axis scatter). */
  rightYKey?: string
  /** Reference horizontal lines (e.g. mean / target). */
  referenceLines?: { y: number; label?: string; color?: string }[]
  /** Linear-regression line of best fit through the first series. */
  bestFitLine?: boolean
}

function bestFit(points: Array<[number, number]>): { slope: number; intercept: number } {
  const n = points.length
  if (n < 2) return { slope: 0, intercept: 0 }
  const sumX = points.reduce((s, [x]) => s + x, 0)
  const sumY = points.reduce((s, [, y]) => s + y, 0)
  const sumXY = points.reduce((s, [x, y]) => s + x * y, 0)
  const sumXX = points.reduce((s, [x]) => s + x * x, 0)
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX || 1)
  const intercept = (sumY - slope * sumX) / n
  return { slope, intercept }
}

export function ScatterChart({
  series, data,
  xKey = "x", yKey = "y", zKey, zRange = [40, 240],
  palette = DEFAULT_PALETTE,
  showLegend = true, showGrid = true, showTooltip = true,
  xLabel, yLabel, compact, className, style, height = "100%", width = "100%",
  showLabels, rightYKey, referenceLines, bestFitLine,
}: ScatterChartProps) {
  const groups: ScatterSeries[] = series?.length ? series : data?.length ? [{ name: "Series 1", data }] : []

  // Compute the best-fit line as a synthetic series of two endpoints across
  // the X domain for the first scatter series.
  const fitLine = (() => {
    if (!bestFitLine || !groups[0]?.data?.length) return null
    const pts = groups[0].data.map(d => [Number(d[xKey]) || 0, Number(d[yKey]) || 0] as [number, number])
    const xs = pts.map(p => p[0])
    const xmin = Math.min(...xs), xmax = Math.max(...xs)
    const { slope, intercept } = bestFit(pts)
    return [
      { [xKey]: xmin, [yKey]: slope * xmin + intercept },
      { [xKey]: xmax, [yKey]: slope * xmax + intercept },
    ] as ChartDatum[]
  })()

  return (
    <ResponsiveContainer width={width} height={height} className={className} style={style}>
      <RScatterChart margin={{ top: 8, right: 16, left: 0, bottom: 8 }}>
        {showGrid && !compact && <CartesianGrid strokeDasharray="3 3" strokeOpacity={0.18} />}
        <XAxis type="number" dataKey={xKey} name={xLabel || xKey} tick={{ fontSize: 10.5 }} label={xLabel ? { value: xLabel, position: "insideBottom", offset: -4 } : undefined} />
        <YAxis yAxisId="left" type="number" dataKey={yKey} name={yLabel || yKey} tick={{ fontSize: 10.5 }} label={yLabel ? { value: yLabel, angle: -90, position: "insideLeft" } : undefined} />
        {rightYKey && <YAxis yAxisId="right" orientation="right" type="number" dataKey={rightYKey} tick={{ fontSize: 10.5 }} />}
        {zKey && <ZAxis type="number" dataKey={zKey} range={zRange} />}
        {showTooltip && <Tooltip cursor={{ strokeDasharray: "3 3" }} contentStyle={{ background: "hsl(var(--card))", border: "1px solid hsl(var(--border))", borderRadius: 6, fontSize: 11, color: "hsl(var(--card-foreground))" }} />}
        {showLegend && !compact && <Legend wrapperStyle={{ fontSize: 11 }} />}
        {referenceLines?.map((r, i) => (
          <ReferenceLine key={`rl-${i}`} yAxisId="left" y={r.y} stroke={r.color || "#888"} strokeDasharray="4 4" label={r.label ? { value: r.label, fontSize: 10 } : undefined} />
        ))}
        {groups.map((g, i) => (
          <Scatter
            key={g.name}
            name={g.name}
            data={g.data}
            fill={g.color || palette[i % palette.length]}
            shape={g.shape || "circle"}
            line={g.withLine ? { stroke: g.color || palette[i % palette.length], strokeWidth: 1.5 } : undefined}
            yAxisId="left"
          >
            {g.cellColors?.map((c, idx) => <Cell key={idx} fill={c} />)}
            {showLabels && <LabelList dataKey={xKey} position="top" style={{ fontSize: 10 }} />}
          </Scatter>
        ))}
        {fitLine && (
          <Scatter name="Best fit" data={fitLine} line={{ stroke: palette[0], strokeWidth: 2 }} shape={() => <g />} legendType="line" yAxisId="left" />
        )}
        {/* Suppress TS unused-import warning for Line */}
        {false && <Line dataKey={xKey} />}
      </RScatterChart>
    </ResponsiveContainer>
  )
}

/** Bubble plot preset — scatter where the Z dimension drives bubble size. */
export function BubbleChart(props: ScatterChartProps) {
  return <ScatterChart zKey={props.zKey || "z"} {...props} />
}
